import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { fetchSageExports, fetchSageDeltaStats, fetchJson, generateSageExport } from '../api.js';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  LinkButton,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  SectionLabel,
  Segmented,
  Table,
  TableShell,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.js';
import { int, stamp } from '../lib/format.js';

const CONTRACT = [
  { label: 'TVA appliquée', value: 'défaut configurable, surchargée par le taux du produit (fichier/opérateur)' },
  { label: 'Prix retenu', value: 'prix promo en cours, sinon prix regular' },
  { label: 'Prix achat HT', value: 'TTC / (1 + TVA), arrondi 3 décimales' },
  { label: 'Codes-barres', value: 'EAN-13 réels uniquement (aucun code interne généré). Laissé vide si absent.' },
  { label: 'Modes d’export', value: 'Catalogue complet (initialisation) ou Delta (nouveautés & modifications depuis le dernier export).' },
  { label: 'Exclusions & Rejets', value: 'packs, non publiés, sans prix — listés dans _rejects et _excluded.' },
] as const;

interface SageExportConfig {
  readonly defaultRate: 0 | 7 | 10 | 13 | 19;
  readonly vatMode: 'percent' | 'label';
  readonly vatLabels: Record<string, string>;
}

const DEFAULT_CONFIG: SageExportConfig = {
  defaultRate: 19,
  vatMode: 'percent',
  vatLabels: { '0': 'Exonéré', '7': 'TVA 7%', '10': 'TVA 10%', '13': 'TVA 13%', '19': 'TVA 19%' },
};

const RATES = [0, 7, 10, 13, 19] as const;

/** S09 — Sage Export (docs/09 §5): plan → generate → history (locked decision D3). */
export function SageExportPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const listQuery = useQuery({ queryKey: ['sage-exports'], queryFn: fetchSageExports });
  const deltaStatsQuery = useQuery({ queryKey: ['sage-delta-stats'], queryFn: fetchSageDeltaStats });

  const [exportMode, setExportMode] = useState<'full' | 'delta'>('full');
  const [onlyWithBarcode, setOnlyWithBarcode] = useState(false);

  // M1 acceptance: the accountant validates the Sage import on a 50-row test
  // file before any full run — same pipeline, first 50 eligible products
  const generateMutation = useMutation({
    mutationFn: (params: {
      limit?: number;
      mode: 'full' | 'delta';
      onlyWithBarcode: boolean;
      onlyClean?: boolean;
      formatKey?: string;
    }) => generateSageExport(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sage-exports'] });
      void queryClient.invalidateQueries({ queryKey: ['sage-delta-stats'] });
    },
  });

  // TVA export configuration — no accountant wait: default 19 %, format switchable
  const configQuery = useQuery({
    queryKey: ['sage-config'],
    queryFn: () => fetchJson<SageExportConfig>('/api/v1/sage-config'),
  });
  const [configDraft, setConfigDraft] = useState<SageExportConfig | null>(null);
  const config: SageExportConfig = configDraft ?? configQuery.data ?? DEFAULT_CONFIG;
  const configDirty = configDraft !== null;
  const saveConfig = useMutation({
    mutationFn: () =>
      fetchJson('/api/v1/sage-config', { method: 'PUT', body: JSON.stringify(config) }),
    onSuccess: () => {
      setConfigDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['sage-config'] });
    },
  });

  const history = listQuery.data ?? [];
  const last = history[0];
  const deltaStats = deltaStatsQuery.data;
  const generated = generateMutation.data;

  const targetCount = exportMode === 'delta' ? (deltaStats?.deltaCount ?? 0) : (deltaStats?.totalEligible ?? 0);
  const targetCleanCount =
    exportMode === 'delta'
      ? (deltaStats?.readyForSageDeltaCount ?? 0)
      : (deltaStats?.readyForSageCount ?? 0);

  return (
    <div>
      <PageHeader
        index="S09"
        title="Export Sage 100c"
        purpose="Génère le fichier d'import Sage 100c (Référence, Désignation, Famille, Marque, Code barre, Tva, Prix achat HT, Prix Vente HT, Prix Vente TTC). Supporte l'export propre direct pour Sage, le catalogue complet et le delta mensuel."
        meta={[
          { label: 'Exports', value: int(history.length) },
          {
            label: 'Dernier',
            value: last === undefined ? 'aucun' : `#${last.id} · ${stamp(last.createdAt)}`,
          },
          { label: 'Éligibles', value: deltaStats ? int(deltaStats.totalEligible) : '…' },
          { label: 'Prêts Sage', value: deltaStats ? int(deltaStats.readyForSageCount ?? 0) : '…' },
          { label: 'Delta Sage', value: deltaStats ? int(deltaStats.readyForSageDeltaCount ?? 0) : '…' },
          { label: 'Delta Audit', value: deltaStats ? int(deltaStats.deltaCount) : '…' },
          { label: 'TVA défaut', value: `${config.defaultRate} %` },
        ]}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="md"
              disabled={generateMutation.isPending}
              onClick={() =>
                generateMutation.mutate({ limit: 50, mode: exportMode, onlyWithBarcode })
              }
            >
              {generateMutation.isPending ? 'Génération…' : 'Test 50 produits'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="md"
              disabled={generateMutation.isPending}
              onClick={() =>
                generateMutation.mutate({ mode: exportMode, onlyWithBarcode })
              }
            >
              {generateMutation.isPending
                ? 'Génération…'
                : `Export Audit (${int(targetCount)})`}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={generateMutation.isPending || targetCleanCount === 0}
              onClick={() =>
                generateMutation.mutate({
                  mode: exportMode,
                  onlyWithBarcode: true,
                  onlyClean: true,
                  formatKey: 'clean-articles',
                })
              }
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-sm"
            >
              {generateMutation.isPending
                ? 'Génération…'
                : exportMode === 'delta'
                  ? `⭐ Export Sage Propre Delta (${int(targetCleanCount)} màj)`
                  : `⭐ Export Sage Propre (${int(targetCleanCount)} prêts)`}
            </Button>
          </>
        }
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          {/* Clean Export Hero Banner / Card */}
          <div className="relative overflow-hidden rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-card to-card p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1.5 max-w-xl">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center rounded-md bg-emerald-500/20 text-emerald-400 p-1 text-sm">
                    ⭐
                  </span>
                  <h3 className="text-sm font-semibold text-ink">Export Sage Propre (Direct Stock & Caisse)</h3>
                  <Badge tone="good">Format 100c prêt</Badge>
                </div>
                <p className="text-xs text-ink-soft leading-relaxed">
                  Filtre strictement les articles ayant validé la <strong>Couche 2 (100% Confirmé EAN)</strong>, avec un <strong>code-barres réel (EAN-13)</strong> et un <strong>prix d'achat HT positif</strong>. Les 9 colonnes métier sont incluses (avec Famille principale) sans aucune colonne technique d'audit.
                </p>
                <div className="flex flex-wrap items-center gap-1.5 pt-1 font-mono text-[11px]">
                  <span className="text-ink-soft mr-1">9 colonnes :</span>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-ink-faint">Reference</code>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-ink-faint">Designation</code>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-emerald-500 font-semibold">Famille</code>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-ink-faint">Marque</code>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-emerald-500 font-semibold">Code barre</code>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-ink-faint">Tva %</code>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-emerald-500 font-semibold">Prix achat HT</code>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-ink-faint">Prix Vente HT</code>
                  <code className="rounded bg-canvas px-1.5 py-0.5 text-ink-faint">Prix Vente TTC</code>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row md:flex-col items-start sm:items-center md:items-end gap-2.5 shrink-0">
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  disabled={generateMutation.isPending || targetCleanCount === 0}
                  onClick={() =>
                    generateMutation.mutate({
                      mode: exportMode,
                      onlyWithBarcode: true,
                      onlyClean: true,
                      formatKey: 'clean-articles',
                    })
                  }
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-sm"
                >
                  {generateMutation.isPending
                    ? 'Génération…'
                    : exportMode === 'delta'
                      ? `⭐ Exporter Sage Propre Delta (${int(targetCleanCount)} màj)`
                      : `⭐ Exporter Sage Propre (${int(targetCleanCount)} prêts)`}
                </Button>
                <span className="text-[11px] text-ink-faint">
                  Génère CSV Sage 100c + Excel XLSX
                </span>
              </div>
            </div>

            {/* KPI Cards */}
            {deltaStats && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 border-t border-border/60 pt-3">
                <div className="rounded-lg bg-canvas/60 p-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-faint">Catalogue Éligible</div>
                  <div className="mt-0.5 text-base font-semibold font-mono text-ink">{int(deltaStats.totalEligible)}</div>
                </div>
                <div className="rounded-lg bg-canvas/60 p-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-faint">Avec EAN-13 Réel</div>
                  <div className="mt-0.5 text-base font-semibold font-mono text-ink">{int(deltaStats.withBarcodeCount)}</div>
                </div>
                <div className="rounded-lg bg-sky-500/10 border border-sky-500/20 p-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-sky-500 font-semibold">Validés Couche 2</div>
                  <div className="mt-0.5 text-base font-semibold font-mono text-sky-500">{int(deltaStats.withLayer2Count ?? 0)}</div>
                </div>
                <div className="rounded-lg bg-canvas/60 p-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-faint">Avec Prix d&apos;achat</div>
                  <div className="mt-0.5 text-base font-semibold font-mono text-ink">{int(deltaStats.withPurchasePriceCount ?? 0)}</div>
                </div>
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 font-semibold">
                    {exportMode === 'delta' ? 'Prêts Sage (Delta)' : 'Prêts pour Sage'}
                  </div>
                  <div className="mt-0.5 text-base font-bold font-mono text-emerald-400">{int(targetCleanCount)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Export Mode & Options Card */}
          <Panel>
            <PanelHeader
              index="MODE"
              title="Paramètres de l'export"
              hint="Choisissez le périmètre et les critères de sélection"
            />
            <PanelBody className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-ink">Périmètre de l'export :</span>
                  <p className="text-xs text-ink-soft">
                    {exportMode === 'full'
                      ? "Initialisation ou réalignement complet de la base Sage 100c."
                      : `Uniquement les nouveautés et validations depuis le dernier export (#${(deltaStats?.lastCleanExport ?? deltaStats?.lastExport)?.id ?? '?'} du ${(deltaStats?.lastCleanExport ?? deltaStats?.lastExport)?.createdAt ? stamp((deltaStats?.lastCleanExport ?? deltaStats?.lastExport)!.createdAt) : 'aucun'}).`}
                  </p>
                </div>
                <Segmented
                  value={exportMode}
                  onChange={(val) => setExportMode(val as 'full' | 'delta')}
                  options={[
                    {
                      value: 'full',
                      label: 'Catalogue complet',
                      ...(deltaStats !== undefined ? { count: deltaStats.totalEligible } : {}),
                    },
                    {
                      value: 'delta',
                      label: 'Delta (Mises à jour)',
                      ...(deltaStats !== undefined ? { count: deltaStats.deltaCount } : {}),
                    },
                  ]}
                />
              </div>

              <div className="border-t border-border pt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <Checkbox
                    checked={onlyWithBarcode}
                    onChange={(e) => setOnlyWithBarcode(e.target.checked)}
                    label={<span className="font-medium text-ink">Exclure les articles sans code-barres</span>}
                  />
                  <p className="text-xs text-ink-soft pl-6.5">
                    Utilise exclusivement les EAN-13 réels des fournisseurs / WooCommerce (aucun faux code 200... injecté).
                  </p>
                </div>
                {deltaStats !== undefined && (
                  <div className="flex items-center gap-2 text-xs font-mono text-ink-soft">
                    <Badge tone="muted">
                      {int(deltaStats.withBarcodeCount)} avec EAN-13 réel
                    </Badge>
                    <Badge tone={onlyWithBarcode ? 'alarm' : 'muted'}>
                      {int(deltaStats.withoutBarcodeCount)} sans EAN
                    </Badge>
                  </div>
                )}
              </div>
            </PanelBody>
          </Panel>

          {generateMutation.isError && (
            <Alert tone="alarm" title="Génération impossible">
              {String(generateMutation.error.message)}
            </Alert>
          )}

          {generated !== undefined && (
            <Alert
              tone="good"
              title={`Export #${generated.id} (${generated.formatKey === 'clean-articles' ? '⭐ Export Sage Propre' : generated.mode === 'delta' ? 'Delta' : 'Catalogue complet'}) généré avec succès`}
              actions={
                <div className="flex flex-wrap items-center gap-2">
                  {generated.csvFile !== null && (
                    <LinkButton
                      href={`/api/v1/sage-exports/${generated.id}/download`}
                      variant="primary"
                      size="sm"
                    >
                      Télécharger CSV ({int(generated.includedCount)} lignes)
                    </LinkButton>
                  )}
                  {generated.xlsxFile && (
                    <LinkButton
                      href={`/api/v1/sage-exports/${generated.id}/download-xlsx`}
                      variant="outline"
                      size="sm"
                    >
                      Télécharger Excel (.xlsx)
                    </LinkButton>
                  )}
                  {generated.rejectedCount > 0 && (
                    <LinkButton
                      href={`/api/v1/sage-exports/${generated.id}/download-rejects`}
                      variant="outline"
                      size="sm"
                    >
                      Télécharger Rejets ({generated.rejectedCount})
                    </LinkButton>
                  )}
                  <LinkButton
                    href={`/api/v1/sage-exports/${generated.id}/download-excluded`}
                    variant="ghost"
                    size="sm"
                  >
                    Télécharger Exclus
                  </LinkButton>
                </div>
              }
            >
              <span className="tabular font-mono text-xs">
                {int(generated.includedCount)} lignes incluses · {int(generated.rejectedCount)}{' '}
                rejets ({int(generated.bundleExcluded)} packs/bundles) · TVA {generated.vatPercent} %
                {generated.formatKey === 'clean-articles' && ' · Format propre direct Sage'}
              </span>
            </Alert>
          )}

          <Panel>
            <PanelHeader
              index="S09"
              title="Historique des exports"
              hint="chaque fichier est rejouable et auditable"
            />
            {listQuery.data !== undefined && history.length === 0 && (
              <PanelBody>
                <EmptyState
                  title="Aucun export pour le moment"
                  hint="Ingérez d'abord le catalogue (S04), puis générez le brouillon : les exclusions seront listées dans le fichier _rejects."
                />
              </PanelBody>
            )}
            {history.length === 0 && listQuery.data === undefined && (
              <PanelBody>
                <p className="text-sm text-ink-soft">Lecture de l&apos;historique…</p>
              </PanelBody>
            )}
            {history.length > 0 && (
              <TableShell plain>
                <Table>
                  <THead>
                    <tr>
                      <Th>#</Th>
                      <Th>Généré le</Th>
                      <Th>Mode</Th>
                      <Th>Format</Th>
                      <Th align="right">Inclus</Th>
                      <Th align="right">Rejets</Th>
                      <Th align="right">Packs</Th>
                      <Th align="right">TVA</Th>
                      <Th align="right">Téléchargements</Th>
                    </tr>
                  </THead>
                  <TBody>
                    {history.map((item) => (
                      <Tr key={item.id}>
                        <Td className="tabular font-mono text-xs text-ink-soft">{item.id}</Td>
                        <Td className="font-mono text-xs text-ink-soft">{stamp(item.createdAt)}</Td>
                        <Td>
                          <Badge tone={item.mode === 'delta' ? 'scan' : 'muted'}>
                            {item.mode === 'delta' ? 'Delta' : 'Complet'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge tone={item.formatKey === 'clean-articles' ? 'good' : 'muted'}>
                            {item.formatKey === 'clean-articles' ? '⭐ Propre' : item.formatKey}
                          </Badge>
                        </Td>
                        <Td align="right" className="tabular font-mono text-ink">
                          {int(item.includedCount)}
                        </Td>
                        <Td align="right" className="tabular font-mono">
                          <span
                            className={item.rejectedCount > 0 ? 'text-clay-600 font-medium' : 'text-ink-soft'}
                          >
                            {int(item.rejectedCount)}
                          </span>
                        </Td>
                        <Td align="right" className="tabular font-mono text-ink-soft">
                          {int(item.bundleExcluded)}
                        </Td>
                        <Td align="right" className="tabular font-mono text-ink-soft">
                          {item.vatPercent} %
                        </Td>
                        <Td align="right">
                          <div className="flex items-center justify-end gap-1.5">
                            {item.csvFile !== null && (
                              <LinkButton
                                href={`/api/v1/sage-exports/${item.id}/download`}
                                variant="outline"
                                size="xs"
                              >
                                CSV
                              </LinkButton>
                            )}
                            {item.xlsxFile && (
                              <LinkButton
                                href={`/api/v1/sage-exports/${item.id}/download-xlsx`}
                                variant="outline"
                                size="xs"
                              >
                                Excel
                              </LinkButton>
                            )}
                            {item.rejectedCount > 0 && (
                              <LinkButton
                                href={`/api/v1/sage-exports/${item.id}/download-rejects`}
                                variant="ghost"
                                size="xs"
                                className="text-amber-700 hover:text-amber-800"
                              >
                                Rejets
                              </LinkButton>
                            )}
                            <LinkButton
                              href={`/api/v1/sage-exports/${item.id}/download-excluded`}
                              variant="ghost"
                              size="xs"
                              className="text-ink-soft hover:text-ink"
                            >
                              Exclus
                            </LinkButton>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </TableShell>
            )}
          </Panel>
        </div>
        <Panel>
          <PanelHeader index="§" title="Contrat de génération" />
          <PanelBody className="space-y-3">
            <SectionLabel>Règles appliquées</SectionLabel>
            <dl className="space-y-2">
              {CONTRACT.map((rule) => (
                <div key={rule.label}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-faint">
                    {rule.label}
                  </dt>
                  <dd className="text-xs leading-relaxed text-ink">{rule.value}</dd>
                </div>
              ))}
            </dl>

            {/* TVA export configuration — client decision 2026-10-03 */}
            <div className="border-t border-border pt-3">
              <SectionLabel>Configuration TVA de l&apos;export</SectionLabel>
              <p className="mt-1 text-xs leading-relaxed text-ink-soft">
                Défaut appliqué aux produits sans taux fichier/opérateur. En mode
                « libellés Sage », la colonne Tva reçoit le nom du taux tel qu&apos;il
                existe dans les réglages Sage (ex. <code>TVA 19%</code>) — à faire
                correspondre exactement aux taux de votre Sage.
              </p>
              <div className="mt-2.5 space-y-2.5">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-ink-soft">TVA par défaut :</span>
                  {RATES.map((rate) => (
                    <Button
                      key={rate}
                      type="button"
                      size="xs"
                      variant={config.defaultRate === rate ? 'primary' : 'outline'}
                      onClick={() => setConfigDraft({ ...config, defaultRate: rate })}
                    >
                      {rate} %
                    </Button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-ink-soft">Colonne Tva :</span>
                  <Button
                    type="button"
                    size="xs"
                    variant={config.vatMode === 'percent' ? 'primary' : 'outline'}
                    onClick={() => setConfigDraft({ ...config, vatMode: 'percent' })}
                  >
                    Pourcentage (19)
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    variant={config.vatMode === 'label' ? 'primary' : 'outline'}
                    onClick={() => setConfigDraft({ ...config, vatMode: 'label' })}
                  >
                    Libellés Sage (TVA 19%)
                  </Button>
                </div>
                {config.vatMode === 'label' && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {RATES.map((rate) => (
                      <label key={rate} className="flex items-center gap-2 text-xs">
                        <span className="w-12 font-mono text-ink-soft">{rate} %</span>
                        <input
                          value={config.vatLabels[String(rate)] ?? ''}
                          onChange={(event) =>
                            setConfigDraft({
                              ...config,
                              vatLabels: { ...config.vatLabels, [String(rate)]: event.target.value },
                            })
                          }
                          className="h-8 flex-1 rounded-lg border border-border bg-card px-2.5 text-xs text-ink"
                        />
                      </label>
                    ))}
                  </div>
                )}
                {configDirty && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      size="xs"
                      disabled={saveConfig.isPending}
                      onClick={() => saveConfig.mutate()}
                    >
                      {saveConfig.isPending ? 'Enregistrement…' : 'Enregistrer la configuration'}
                    </Button>
                    <Button type="button" variant="ghost" size="xs" onClick={() => setConfigDraft(null)}>
                      Annuler
                    </Button>
                    <span className="text-[10px] text-ink-faint">
                      s&apos;applique au prochain export (le fichier déjà généré ne change pas)
                    </span>
                  </div>
                )}
                {saveConfig.isError && (
                  <Alert tone="alarm" title="Échec de l'enregistrement">
                    {String(saveConfig.error.message)}
                  </Alert>
                )}
              </div>
            </div>

            <Alert tone="warn" title="Avant d'importer dans Sage">
              Sauvegardez la base Sage (docs/08 §9), importez en mode test, puis contrôlez le
              compteur de lignes rejetées avant de valider en production.
            </Alert>
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
}
